import React, { useEffect, useMemo, useState } from "react";

import { useNavigate, useParams } from "react-router-dom";

import { doc, getDoc, onSnapshot, query, collection, where } from "firebase/firestore";

import { ArrowLeft } from "lucide-react";

import { HomeLayout } from "../components/layout/HomeLayout";

import { StudentTasksHub } from "../components/dashboard/StudentTasksHub";

import { TeacherWorkshopPanel } from "../components/dashboard/TeacherWorkshopPanel";

import { useBordonaAccount } from "../hooks/useBordonaAccount";

import { useStudentAuth } from "../hooks/useStudentAuth";

import { useStudentClassrooms } from "../hooks/useStudentClassrooms";

import { useTeacherId } from "../hooks/useTeacherId";

import { useTeacherClassrooms } from "../hooks/useTeacherClassrooms";

import { db } from "../../firebase/config";

import { normalizeClassroomCode, type Classroom, type ClassroomMember } from "../lib/classroomEngine";

import { HOME_THEME as C } from "../lib/homeTheme";



export const ClassroomDetailPage: React.FC = () => {

  const { code: rawCode } = useParams<{ code: string }>();

  const code = normalizeClassroomCode(rawCode || "");

  const navigate = useNavigate();

  const { role } = useBordonaAccount();

  const { profile, user } = useStudentAuth();

  const { teacherId, teacherName } = useTeacherId();

  const studentId = profile?.uid ?? user?.uid ?? "";

  const studentName = profile

    ? `${profile.firstName} ${profile.lastName}`.trim()

    : user?.displayName?.trim() || "Estudiante";



  const isTeacher = role === "teacher";

  const { memberships } = useStudentClassrooms(studentId || undefined, studentName);

  const { membersByClassroom } = useTeacherClassrooms(teacherId, teacherName);



  const [classroom, setClassroom] = useState<Classroom | null>(null);

  const [roster, setRoster] = useState<ClassroomMember[]>([]);

  const [loading, setLoading] = useState(true);

  const [accessDenied, setAccessDenied] = useState(false);



  useEffect(() => {

    if (!code) {

      setLoading(false);

      setAccessDenied(true);

      return;

    }



    let cancelled = false;



    getDoc(doc(db, "bordona_classrooms", code)).then((snap) => {

      if (cancelled) return;

      if (!snap.exists()) {

        setAccessDenied(true);

        setLoading(false);

        return;

      }

      setClassroom({ id: snap.id, ...snap.data() } as Classroom);

      setLoading(false);

    });



    const q = query(

      collection(db, "bordona_classroom_members"),

      where("classroomCode", "==", code)

    );

    const unsub = onSnapshot(q, (snap) => {

      setRoster(snap.docs.map((d) => ({ id: d.id, ...d.data() } as ClassroomMember)));

    });



    return () => {

      cancelled = true;

      unsub();

    };

  }, [code]);



  const canAccess = useMemo(() => {

    if (!classroom || !code) return false;

    if (isTeacher) return classroom.teacherId === teacherId;

    return memberships.some((m) => m.classroomCode === code);

  }, [classroom, code, isTeacher, teacherId, memberships]);



  useEffect(() => {

    if (classroom && !loading && !canAccess) {

      setAccessDenied(true);

    }

  }, [classroom, loading, canAccess]);



  const teacherRoster = isTeacher ? membersByClassroom(code) : roster;



  if (loading) {

    return (

      <HomeLayout>

        <div className="flex h-full items-start justify-start px-6 md:px-12 lg:px-24 pt-32">

          <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-[#c4b5fd] animate-spin" />

        </div>

      </HomeLayout>

    );

  }



  if (accessDenied || !classroom) {

    return (

      <HomeLayout>

        <div className="flex h-full flex-col items-start justify-center gap-6 px-6 md:px-12 lg:px-24">

          <p className="font-light text-xl text-left" style={{ color: C.text }}>

            No tienes acceso a esta clase

          </p>

          <button

            type="button"

            onClick={() => navigate("/clases")}

            className="text-[11px] tracking-[0.14em] uppercase transition-colors hover:text-[#ddd6fe]"

            style={{ color: C.lavender }}

          >

            ← Mis clases

          </button>

        </div>

      </HomeLayout>

    );

  }



  return (

    <HomeLayout>

      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-24 py-10 pb-24">

        <button

          type="button"

          onClick={() => navigate("/clases")}

          className="inline-flex items-center gap-2 text-[11px] tracking-[0.14em] uppercase transition-colors duration-300 hover:text-[#c4b5fd] mb-12"

          style={{ color: C.muted }}

        >

          <ArrowLeft className="h-4 w-4" />

          Mis clases

        </button>



        <header className="max-w-4xl text-left">

          <h1

            className="font-serif font-light tracking-tight text-left text-[clamp(2.5rem,7vw,4rem)] leading-[0.95]"

            style={{ color: C.text }}

          >

            {classroom.title}

          </h1>



          <div className="mt-6 flex flex-wrap items-center gap-4 text-left">

            <span

              className="inline-block px-3 py-1 font-mono text-xs tracking-[0.2em] uppercase"

              style={{ color: C.mint, borderBottom: `1px solid ${C.mintSoft}` }}

            >

              {code}

            </span>

            {!isTeacher && (

              <span className="text-xs tracking-wide" style={{ color: C.muted }}>

                {classroom.teacherName || "Profesor"}

              </span>

            )}

            <span className="text-[10px] tracking-[0.18em] uppercase" style={{ color: C.dim }}>

              {teacherRoster.length} estudiante{teacherRoster.length !== 1 ? "s" : ""}

            </span>

          </div>

        </header>



        <div className="mt-16 lg:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 xl:gap-20">

          {/* Tareas — foco pedagógico */}

          <section className="lg:col-span-7 xl:col-span-8 text-left">

            <h2

              className="mb-8 font-light tracking-tight text-left text-[clamp(1.5rem,3vw,2.25rem)] bg-clip-text text-transparent"

              style={{ backgroundImage: C.textGradient }}

            >

              Tareas de la clase

            </h2>



            {isTeacher ? (

              <TeacherWorkshopPanel classroomCode={code} embedded hideTitle />

            ) : studentId ? (

              <StudentTasksHub variant="index" classroomCode={code} />

            ) : (

              <p className="text-sm text-left" style={{ color: C.muted }}>

                Inicia sesión para ver las tareas de esta clase.

              </p>

            )}



            {isTeacher && (

              <p className="mt-10 text-xs leading-relaxed text-left" style={{ color: C.dim }}>

                Al crear una tarea desde Escalas, Acordes o Canciones, selecciona esta clase ({code}).

              </p>

            )}

          </section>



          {/* Estudiantes — directorio */}

          <aside className="lg:col-span-5 xl:col-span-4 text-left">

            <h2

              className="mb-8 text-[10px] tracking-[0.22em] uppercase text-left"

              style={{ color: C.dim }}

            >

              {isTeacher ? "Estudiantes" : "Compañeros"}

            </h2>



            {teacherRoster.length === 0 ? (

              <p className="text-sm text-left leading-relaxed" style={{ color: C.dim }}>

                {isTeacher

                  ? "Comparte el código de clase para que se unan."

                  : "Aún no hay más estudiantes visibles."}

              </p>

            ) : (

              <ul className="space-y-0">

                {teacherRoster.map((m) => (

                  <li

                    key={m.id}

                    className="flex items-center gap-3 py-3 text-left"

                    style={{ borderBottom: `1px solid ${C.lineItem}` }}

                  >

                    <div

                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-medium uppercase"

                      style={{ background: "rgba(196,181,253,0.12)", color: C.lavender }}

                    >

                      {m.studentName?.charAt(0) || "?"}

                    </div>

                    <span className="truncate text-sm font-light" style={{ color: C.text }}>

                      {m.studentName}

                      {m.studentId === studentId ? (

                        <span style={{ color: C.muted }}> · tú</span>

                      ) : null}

                    </span>

                  </li>

                ))}

              </ul>

            )}

          </aside>

        </div>

      </div>

    </HomeLayout>

  );

};



export default ClassroomDetailPage;

